import React, { useEffect, useState } from "react";
import TemperatureApi from "../../api/TemperatureApi";
import IconJumpToDate from "../../Icons/Temperature/JumpToDate";
import IconResetZoom from "../../Icons/Temperature/ResetZoom";
import IconSettings from "../../Icons/Temperature/Settings";
import IconZoomIn from "../../Icons/Temperature/ZoomIn";
import IconZoomOut from "../../Icons/Temperature/ZoomOut";
import IconZoomY from "../../Icons/Temperature/ZoomY";
import IconLiveTemperature from "../../Icons/Temperature/LiveTemperature";
import { Collection } from "../../Model/Collection";
import { TemperatureSensor } from "../../Model/Temperature";
import DateUtil from "../../Utility/DateUtil";
import CalendarRange from "../Cards/CalendarRange";
import Modal from "../Common/Modal";
import SettingsCard from "./SettingsCard";
import { MemoizedTemperatureChart } from "./TemperatureChart";
import "./TemperatureGraph.css";
import TemperatureSensors from "./TemperatureSensors";
import TemperatureChartUtil, {
  TemperatureSetting,
  EGraphType,
} from "./TemperatureChartUtil";
import { DateRange } from "../../Model/Date";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getModuleTemperatureChanged } from "../../redux/reducers/TemperatureReducer";
import { ETemperatureState } from "../../Model/ETemperatureState";
import moment, { Moment } from "moment";

const initialSetting: TemperatureSetting = {
  showGraph: EGraphType.AllGraph,
  minTemp: 0,
  maxTemp: 25,
  minOutput: -100,
  maxOutput: 100,
  showTooltip: true,
  showTemperatureInfo: true,
  refreshInterval: 60,
  refreshStaleDataInterval: 1,
  useBestYZoom: false,
  xZoom: 2,
};

const TEMPERATURE_SETTING = "TEMPERATURE_SETTING";

function getSetting(): TemperatureSetting {
  const jsonString = localStorage.getItem(TEMPERATURE_SETTING);
  if (jsonString != null) {
    try {
      const json = JSON.parse(jsonString);
      if (json != null) {
        return json as TemperatureSetting;
      }
    } catch (error) {
      console.log(error);
    }
  }
  return initialSetting;
}

function getSensorsVisibility(
  temperatureSensorCollection: Collection<TemperatureSensor>
): { [index: string]: boolean } {
  const setting = getSetting();
  return TemperatureChartUtil.getSensorVisibility(
    setting.showGraph,
    temperatureSensorCollection
  );
}

type Props = {
  moduleIdentifier: string;
  temperatureSensorCollection: Collection<TemperatureSensor>;
  style?: React.CSSProperties;
};

function getInitialDate() {
  let start = new Date();
  start.setHours(0, 0, 0, 0);

  // let end = new Date();
  // end.setHours(23, 59, 59, 999);
  let end = moment().toDate();

  return {
    start,
    end,
  };
}

export default function TemperatureGraph({
  moduleIdentifier,
  temperatureSensorCollection,
  style,
}: Props) {
  const [showSetting, setShowSetting] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [setting, setSetting] = useState(getSetting());
  const [sensorsVisibility, setSensorsVisibility] = useState(
    getSensorsVisibility(temperatureSensorCollection)
  );
  const [averageSensorValue, setAverageSensorValue] = useState(4);
  const [TargetSensorValue, setTargetSensorValue] = useState(4);
  const [availableDates, setAvailableDate] = useState([] as Date[]);
  const maxZoom = 8;
  const [dateRange, setDateRange] = useState(getInitialDate());
  const [isLiveAndMaxSlider, setIsLiveAndMaxSlider] = useState(true);

  const moduleTempChanged = useTypedSelector((state) =>
    getModuleTemperatureChanged(state, moduleIdentifier)
  );

  useEffect(() => {
    localStorage.setItem(TEMPERATURE_SETTING, JSON.stringify(setting));
  }, [setting]);

  useEffect(() => {
    TemperatureApi.getOAvailableTemperatureDataDates(moduleIdentifier).then(
      (dateStrings) => {
        setAvailableDate(dateStrings.map((ds) => new Date(ds)));
      }
    );
  }, [moduleIdentifier]);

  const onZoomOutClicked = () => {
    if (setting.xZoom < maxZoom) {
      setSetting({
        ...setting,
        xZoom: setting.xZoom + 1,
      });
    }
  };

  const onZoomInClicked = () => {
    if (setting.xZoom > 0) {
      setSetting({
        ...setting,
        xZoom: setting.xZoom - 1,
      });
    }
  };

  const onResetClicked = () => {
    if (setting.xZoom !== 2) {
      setSetting({
        ...setting,
        xZoom: 2,
      });
    }
  };

  const onLiveTemperatureClicked = () => {
    setIsLiveAndMaxSlider(true);
  };

  const OnCalendarOk = (dateRange: DateRange) => {
    setDateRange(dateRange);
    setShowCalendar(false);
  };

  const onBestYZoomClicked = () => {
    setSetting({
      ...setting,
      useBestYZoom: !setting.useBestYZoom,
    });
  };

  const onSettingChanged = (newSetting: TemperatureSetting) => {
    setShowSetting(false);

    //-------Sensors Visibility-----//
    let newSensorsVisibility = TemperatureChartUtil.getSensorVisibility(
      newSetting.showGraph,
      temperatureSensorCollection
    );
    if (newSetting.showGraph === EGraphType.Custom) {
      newSensorsVisibility = sensorsVisibility;
    }
    setSensorsVisibility(newSensorsVisibility);

    setSetting({
      ...newSetting,
      useBestYZoom: false,
    });
  };

  console.log("DEBUG", "TemperatureGraph Rendered");

  const gridTemplateColumns = setting.showTemperatureInfo
    ? "calc(100% - 360px) 360px"
    : "100% 0px";

  return (
    <div
      className="temperature-graph-container"
      style={{
        gridTemplateColumns: gridTemplateColumns,
        ...style,
      }}
    >
      <MemoizedTemperatureChart
        style={{
          gridArea: "chart",
        }}
        chartDateRange={dateRange}
        setChartDateRange={setDateRange}
        moduleIdentifier={moduleIdentifier}
        temperatureSensorCollection={temperatureSensorCollection}
        sensorsVisibility={sensorsVisibility}
        temperatureSetting={setting}
        maxXZoom={maxZoom}
        isLiveAndMaxSlider={isLiveAndMaxSlider}
        setIsLiveAndMaxSlider={setIsLiveAndMaxSlider}
        firstDataAvailableDate={availableDates[0]}
      />
      {/* {temperatureChart} */}
      {setting.showTemperatureInfo && (
        <TemperatureSensors
          moduleIdentifier={moduleIdentifier}
          temperatureSensorCollection={temperatureSensorCollection}
          sensorsVisibility={sensorsVisibility}
          temperatureSetting={setting}
          setSensorsVisibility={setSensorsVisibility}
          setTemperatureSetting={setSetting}
          setAverageSensorValue={setAverageSensorValue}
          setTargetSensorValue={setTargetSensorValue}
          style={{
            gridArea: "sensor",
          }}
        />
      )}
      <div className="temperature-graph-control">
        <span
          className={
            moduleTempChanged.state === ETemperatureState.Violation
              ? "temperature-graph-control-average temperature-graph-violation-text"
              : "temperature-graph-control-average"
          }
        >
          {moduleTempChanged.state !== ETemperatureState.Violation &&
            `Average : ${averageSensorValue.toFixed(1)}°C`}
          {moduleTempChanged.state === ETemperatureState.Violation &&
            `Temp Alert : Target ${TargetSensorValue.toFixed(1)}°C at ${
              moment(moduleTempChanged.time).format("YYYY-MM-DD hh:mm A")
            }`}
        </span>

        <div className="temperature-graph-control-button">
          <ImageButton
            id="settingsBtn"
            style={{ position: "relative" }}
            onClick={() => setShowSetting(true)}
            tooltip="Settings"
          >
            <IconSettings />
          </ImageButton>
          <ImageButton id="ZoomIn" onClick={onZoomInClicked} tooltip="Zoom In">
            <IconZoomIn />
          </ImageButton>
          <ImageButton
            id="ZoomOut"
            onClick={onZoomOutClicked}
            tooltip="Zoom Out"
          >
            <IconZoomOut />
          </ImageButton>
          <ImageButton
            id="ResetZoom"
            onClick={onResetClicked}
            tooltip="Reset Zoom"
          >
            <IconResetZoom />
          </ImageButton>
          <ImageButton
            id="LiveTemperature"
            onClick={onLiveTemperatureClicked}
            tooltip="Live Temperature"
            disabled={isLiveAndMaxSlider}
          >
            <IconLiveTemperature />
          </ImageButton>
          <ImageButton
            id="BestYZoom"
            onClick={onBestYZoomClicked}
            isToogled={setting.useBestYZoom}
            tooltip="Best Y Zoom"
          >
            <IconZoomY />
          </ImageButton>
          <ImageButton
            id="calendarBtn"
            onClick={() => setShowCalendar(true)}
            style={{ position: "relative" }}
            tooltip="Jump to Date"
          >
            <IconJumpToDate />
          </ImageButton>
        </div>
        <span className="temperature-graph-control-date-range">
          {`Data available from ${
            availableDates.length > 1
              ? DateUtil.toString(availableDates[0])
              : DateUtil.toString(new Date())
          } to ${DateUtil.toString(new Date())}`}
        </span>
      </div>
      <Modal show={showSetting}>
        <SettingsCard
          setting={setting}
          okHandler={(setting) => onSettingChanged(setting)}
          cancelHandler={() => setShowSetting(false)}
        />
      </Modal>
      <Modal
        show={showCalendar}
        onHide={() => setShowCalendar(false)}
        className="calendar-modal"
      >
        <Modal.Body>
          {showCalendar && (
            <CalendarRange
              availableDates={availableDates}
              dateRange={dateRange}
              onOk={OnCalendarOk}
              onCancel={() => setShowCalendar(false)}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

type ImageButtonProps = {
  id: string;
  onClick: () => void;
  children: React.ReactNode;
  isToogled?: boolean;
  style?: React.CSSProperties;
  tooltip?: string;
  disabled?: boolean;
};

const ImageButton = ({
  id,
  onClick,
  children,
  isToogled = false,
  style = {},
  tooltip = undefined,
  disabled = false,
}: ImageButtonProps) => (
  <button
    data-for="gray-tooltip"
    data-tip={tooltip}
    data-tip-disable={tooltip == undefined}
    data-effect="solid"
    data-place="top"
    className={
      isToogled
        ? "temperature-graph-image-button temperature-graph-image-button-toogled"
        : "temperature-graph-image-button"
    }
    onClick={onClick}
    id={id}
    disabled={disabled}
  >
    <div className="temperature-graph-image-button-content">{children}</div>
  </button>
);
